/**
 * What the organization page's taps tell checkout (GH-809): an amount and a fund, as hints.
 *
 * Both are presentation, never authority. The amount is still priced by the server's quote, and
 * the fund is dropped by `sendableDesignationId` unless the context actually offers it — so a
 * forged link buys nothing but a different preselection on a screen the forger is looking at,
 * the same reasoning as `?intent=pledge`.
 *
 * The appeal rung (GH-937) rides the same way: `?appealRung=` names which amount being called
 * from the stage this gift or pledge answers. It is a hint in exactly the same sense — the quote
 * carries it and the **server** refuses an unknown rung, a closed appeal, a rung the room has
 * moved past, and any amount that is not the rung's own — so a forged value buys a refusal, not
 * a count.
 */

/** Minor units: digits only, no leading zero, and not absurdly long. */
const AMOUNT_HINT = /^[1-9][0-9]{0,9}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The query string to append to a checkout address, or "" when there is nothing to say. */
export function checkoutHints(hints: {
  amountMinor: string | null;
  designationId: string | null;
  /** The appeal rung this gift answers (GH-937); optional so existing callers are untouched. */
  appealRungId?: string | null;
}): string {
  const query = new URLSearchParams();
  if (hints.amountMinor && AMOUNT_HINT.test(hints.amountMinor)) {
    query.set("amount", hints.amountMinor);
  }
  if (hints.designationId && UUID.test(hints.designationId)) {
    query.set("designation", hints.designationId);
  }
  if (hints.appealRungId && UUID.test(hints.appealRungId)) {
    query.set("appealRung", hints.appealRungId);
  }
  const text = query.toString();
  return text ? `?${text}` : "";
}

/** The hints a checkout address carries, each null unless it has the right shape. */
export function readCheckoutHints(search: string): {
  amountMinor: string | null;
  designationId: string | null;
  appealRungId: string | null;
} {
  const params = new URLSearchParams(search);
  const amount = params.get("amount");
  const designation = params.get("designation");
  const appealRung = params.get("appealRung");
  return {
    amountMinor: amount && AMOUNT_HINT.test(amount) ? amount : null,
    designationId: designation && UUID.test(designation) ? designation : null,
    appealRungId: appealRung && UUID.test(appealRung) ? appealRung : null,
  };
}
